/**
 * 상담 진행 순서 — 규칙 테이블.
 *
 * `resolveCounselFlowStage` 의 조기 반환 사슬을 우선순위 규칙으로 옮긴 것이다. 규칙
 * 순서와 조건은 원본과 1:1 로 대응하며, application 의 `counselStageMachine.diff.test.ts`
 * 가 두 구현의 출력이 모든 입력에서 같은지 차등 검증한다.
 *
 * ## 왜 앱이 아니라 여기 있나
 *
 * "다음에 무슨 화면이 오는가" 는 상담 도메인의 성질이지 특정 앱의 성질이 아니다. 앱 안에만
 * 있으면 프리뷰는 순서를 흉내 내야 하고, 흉내 낸 순서는 반드시 진짜와 갈라진다.
 *
 * 갈라 놓은 경계는 이렇다.
 *   - **규칙(여기)**: 판정의 순서와 조건. 기획이 바뀔 때 실제로 손대는 부분.
 *   - **술어(호스트)**: `isStarted`, `remainingCount` 같은 이름의 계산. 프로덕션은
 *     `VisitCounselView` 를 읽고 프리뷰는 자기 상태를 읽지만, 이름과 의미는 같다.
 */

import type { StageMachine } from '@miso/design-system';

/** 규칙이 부르는 술어 이름. 호스트는 이 이름 전부에 구현을 붙여야 한다. */
export const COUNSEL_PREDICATE_NAMES = [
  'isStarted',
  'hasSketchingData',
  'entranceTarget',
  'hasEntranceTarget',
  'remainingCount',
  'progressSub',
  'hasSketchVideo',
  'hasPendingAdditionalSpaceWork',
] as const;

export type CounselPredicateName = (typeof COUNSEL_PREDICATE_NAMES)[number];

export const counselStageMachine: StageMachine = {
  id: 'counsel.stageMachine',
  predicates: [...COUNSEL_PREDICATE_NAMES],
  rules: [
    // 상담이 시작되지 않았으면 시작 화면.
    { when: { '==': [{ var: 'isStarted' }, false] }, stage: 'start' },

    // 방 구조가 아직 없으면 구조 기록.
    { when: { '==': [{ var: 'hasSketchingData' }, false] }, stage: 'house-structure' },

    // 현관은 항상 우선 자동 진행 — "현관은 미리 짐기록 후 나머지 방만 플로우 진입" 정책.
    {
      when: { '==': [{ var: 'hasEntranceTarget' }, true] },
      stage: 'moving-items',
      target: { $var: 'entranceTarget' },
    },

    // 현관 외 미완료가 하나라도 남으면 사용자가 직접 고른다.
    // 1개일 때 자동 진입하면 뒤로가기가 상담 상세로 빠지는 회귀가 있어 항상 picker 로 보낸다.
    { when: { '>=': [{ var: 'remainingCount' }, 1] }, stage: 'pick-room' },

    // 진행하기 시퀀스가 안 끝났으면 해당 sub-stage.
    {
      when: { '!=': [{ var: 'progressSub' }, 'completed'] },
      stage: 'progress',
      sub: { $var: 'progressSub' },
    },

    // 스케치 영상이 추가공간보다 먼저 — 옛 ProgressPage 체크리스트 순서와 정렬.
    { when: { '==': [{ var: 'hasSketchVideo' }, false] }, stage: 'sketch-video' },

    {
      when: { '==': [{ var: 'hasPendingAdditionalSpaceWork' }, true] },
      stage: 'additional-spaces',
    },

    { stage: 'done' },
  ],
  documents: {
    start: 'counsel.start',
    'house-structure': 'counsel.houseStructure',
    'pick-room': 'counsel.pickRoom',
    'moving-items': 'counsel.movingItems',
    progress: 'counsel.progress',
    'sketch-video': 'counsel.sketchVideo',
    'additional-spaces': 'counsel.additionalSpaces',
    done: 'counsel.complete',
  },
};

/** 진행하기 시퀀스의 sub-stage 순서. 리뷰 라우팅·프리뷰 러너가 같은 어휘를 쓴다. */
export const PROGRESS_SUB_STAGES = [
  'moving-date',
  'departure-transport',
  'arrival-address',
  'arrival-transport',
  'packing',
  'moving-types',
  'customer-requests',
] as const;

export type ProgressSubStageKind = (typeof PROGRESS_SUB_STAGES)[number];
